import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
	getGetConfigQueryOptions,
	getGetRunQueryOptions,
	getListAuditEventsQueryOptions,
} from '@/api/generated/endpoints';
import { RouteErrorFallback } from '@/components/route-error-fallback';
import { Button } from '@/components/ui/button';
import { sessionQueryOptions } from '@/features/auth';
import { RunApprovalActions } from '@/features/runs/components/run-approval-actions';
import { RunDetail } from '@/features/runs/components/run-detail';
import { toRunAuditTrailParams } from '@/features/runs/utils/audit-trail';
import { isRunEditable } from '@/features/runs/utils/run-editability';

export const Route = createFileRoute('/_authenticated/runs/$runId/')({
	loader: ({ context: { queryClient }, params }) => {
		void queryClient.ensureQueryData(getGetRunQueryOptions(params.runId));
		void queryClient.ensureQueryData(getListAuditEventsQueryOptions(toRunAuditTrailParams(params.runId)));
		// The advisory join-approval indicator reads the live config (ADR-0016).
		void queryClient.ensureQueryData(getGetConfigQueryOptions());
	},
	errorComponent: (props) => <RouteErrorFallback {...props} fallbackMessageKey='error_load_run_details' />,
	component: RunDetailRoute,
});

function RunDetailRoute() {
	const { t } = useTranslation();
	const { runId } = Route.useParams();
	// Already ensured by the _authenticated layout's beforeLoad — resolves from cache.
	const { data: session } = useSuspenseQuery(sessionQueryOptions);

	return (
		<RunDetail
			runId={runId}
			actorRole={session.user.role}
			correctAction={(run) => (
				<Link to='/runs/$runId/edit' params={{ runId: run.id }}>
					{t('runs_correct_data_button')}
				</Link>
			)}
			runLink={(linkedRunId) => (
				<Link to='/runs/$runId' params={{ runId: linkedRunId }} className='underline underline-offset-2'>
					{t('runs_detail_run_link')}
				</Link>
			)}
			actions={(run) => (
				<>
					<Button asChild variant='outline'>
						<Link to='/runs'>
							<ArrowLeft className='size-4' />
							{t('runs_back_to_list')}
						</Link>
					</Button>
					{run.kind === 'group_creation' ? (
						<Button asChild variant='outline'>
							<Link to='/runs/$runId/invite-ops' params={{ runId: run.id }}>
								{t('runs_invite_ops_button')}
							</Link>
						</Button>
					) : null}
					{isRunEditable(run.status) ? (
						<Button asChild variant='outline'>
							<Link to='/runs/$runId/edit' params={{ runId: run.id }}>
								{t('runs_edit_button')}
							</Link>
						</Button>
					) : null}
					<RunApprovalActions run={run} actorRole={session.user.role} />
				</>
			)}
		/>
	);
}
